import { FilterConfig, MainFilter, SelectOption } from 'components/tables/list-table/ListTableTypes';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetJobRolesWithFiltersQuery } from 'store/api/jobRoles';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { MatterStateType } from 'types/matters';
import { TimecardStatusType } from 'types/timecards';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { PERIOD_SELECTOR } from './reportPeriods';

export { PERIOD_SELECTOR, periodQuickFilter } from './reportPeriods';
export type { ReportPeriod } from './reportPeriods';

/**
 * Filter-bar pieces the reports share. A report's backend reads `period` and `dateBasis` inside its SQL, and
 * every other selector as a filter on its rows, so the selectors here are the report API's contract.
 */

export const DATE_BASIS_SELECTOR = 'dateBasis';

/** The case statuses in the order a case moves through them: the offer, its outcome, then the work. */
export const CASE_STATUS_ORDER: string[] = [
    MatterStateType.OFFER_PENDING,
    MatterStateType.OFFER_WON,
    MatterStateType.OFFER_LOST,
    MatterStateType.IN_PROGRESS,
    MatterStateType.COMPLETED,
];

/** The timecard statuses in the order a timecard moves through them, as the timecards list shows them. */
export const TIMECARD_STATUS_ORDER: string[] = [
    TimecardStatusType.DRAFT,
    TimecardStatusType.FINISHED,
    TimecardStatusType.DRAFT_INVOICE,
    TimecardStatusType.INVOICED,
    TimecardStatusType.PAID,
];

/**
 * Status options in the order of `order`, the order a record moves through the statuses, not A to Z. A status
 * `order` does not name keeps its place after the named ones.
 */
export function inStatusOrder(options: SelectOption[], order: string[]): SelectOption[] {
    const rank = (option: SelectOption) => {
        const index = order.indexOf(String(option.value));
        return index === -1 ? order.length : index;
    };
    return [...options].sort((left, right) => rank(left) - rank(right));
}

export function periodFilter(labelId = 'reporting.filter.period'): MainFilter {
    return { type: 'daterange', label: t(labelId), selector: PERIOD_SELECTOR, shouldDisplayQuickPick: true };
}

/** Which date the period bounds: the work date of the timecards (the report's default) or the invoice date. */
export function dateBasisFilter(): MainFilter {
    return singleChoiceFilter(DATE_BASIS_SELECTOR, 'reporting.filter.date-basis', [
        { label: t('reporting.filter.date-basis-timecard-date'), value: 'TIMECARD_DATE' },
        { label: t('reporting.filter.date-basis-invoice-date'), value: 'INVOICE_DATE' },
    ]);
}

export function clientFilter(selector = 'clientId', labelId = 'reporting.filter.client'): MainFilter {
    return {
        type: 'dynamicmultiselect',
        subType: 'contact',
        label: t(labelId),
        selector,
        paginated: {
            fetch: useLazyGetMinContactsListWithFiltersQuery,
            searchField: ['name', 'partyNumber'],
            getOptionIdentifier: 'id',
            getOptionLabel: 'name',
        },
    } as MainFilter;
}

export function caseFilter(selector = 'matterId'): MainFilter {
    return {
        type: 'dynamicmultiselect',
        label: t('reporting.filter.case'),
        selector,
        getOptionStartAdornment: renderMatterIndicators,
        paginated: {
            fetch: useLazyGetMattersListQuery,
            searchField: ['fullName', 'clientParty.name'],
            getOptionIdentifier: 'id',
            getOptionLabel: 'fullName',
            groupByField: 'clientParty.name',
            sort: ['clientParty.name,asc', 'name,asc'],
        },
    } as MainFilter;
}

export function employeeFilter(selector: string, labelId: string): MainFilter {
    return {
        type: 'dynamicmultiselect',
        label: t(labelId),
        selector,
        paginated: {
            fetch: useLazyGetAllEmployeesListWithFiltersQuery,
            searchField: 'fullName',
            getOptionIdentifier: 'id',
            getOptionLabel: 'name',
        },
    } as MainFilter;
}

export function textFilter(selector: string, labelId: string): MainFilter {
    return { type: 'text', label: t(labelId), selector, comperator: '=lk=' };
}

/** A pick list over fixed options, such as classifier codes; the backend matches the option values. */
export function optionsFilter(selector: string, labelId: string, options: SelectOption[]): MainFilter {
    return { type: 'multiselect', label: t(labelId), selector, options };
}

/** A yes / no question on a boolean column of the report's rows. */
export function yesNoFilter(selector: string, labelId: string): MainFilter {
    return singleChoiceFilter(
        selector,
        labelId,
        [
            { label: t('filters.yes'), value: 'true' },
            { label: t('filters.no'), value: 'false' },
        ],
        { answerWithLabel: true }
    );
}

export function isActiveFilter(): MainFilter {
    return yesNoFilter('isActive', 'reporting.filter.is-active');
}

export function jobRoleFilter(selector = 'jobRoleId'): MainFilter {
    return {
        type: 'dynamicmultiselect',
        label: t('reporting.filter.job-role'),
        selector,
        paginated: {
            fetch: useLazyGetJobRolesWithFiltersQuery,
            searchField: 'name',
            getOptionIdentifier: 'id',
            getOptionLabel: 'name',
        },
    } as MainFilter;
}

export function coreTeamFilter(selector = 'coreTeamId', labelId = 'reporting.filter.core-team'): MainFilter {
    return {
        type: 'dynamicmultiselect',
        label: t(labelId),
        selector,
        paginated: {
            fetch: useLazyGetMinTeamsListWithFiltersQuery,
            searchField: 'name',
            getOptionIdentifier: 'id',
            getOptionLabel: 'name',
        },
    } as MainFilter;
}

/** The search box: a contains-match on each of the report's text columns. */
export function searchSelectors(...selectors: string[]) {
    return selectors.map((selector) => ({ selector, comparator: '=lk=' }));
}

/** The list's starting URL state: main-filter clauses, such as a report's default statuses. */
export function mainFilterDefaults(clauses: string[]): string {
    return new URLSearchParams({ mf: clauses.join(';') }).toString();
}

/**
 * One answer at a time, sent as `selector==value`. A checkbox group rather than a dropdown, so the filter can also
 * show as a quick filter: the list keeps a quick filter in step with a checkbox group or a multiselect on the same
 * selector, not with a dropdown. The drawer draws a divider before every checkbox group but the first, meant for
 * groups laid out side by side; the reports stack their filters in one column, so their configs set
 * `hideFilterDividers`.
 *
 * @param answerWithLabel the quick filter's chip reads `label: answer`, for answers that say nothing on their own
 */
export function singleChoiceFilter(
    selector: string,
    labelId: string,
    options: SelectOption[],
    { answerWithLabel = false }: { answerWithLabel?: boolean } = {}
): MainFilter {
    return {
        type: 'checkboxgroup',
        label: t(labelId),
        selector,
        comperator: '==',
        multiple: false,
        answerWithLabel,
        filters: options.map((option) => ({ label: option.label, argument: `${option.value}` })),
    };
}

type QuickFilter = FilterConfig['quickFilters'][number];

/**
 * The quick filter for one of the report's own filters: the same selector, comparator and options, so the list
 * treats it as a shortcut onto that filter and keeps the two in step. Only a single-choice filter (a checkbox
 * group) or a multiselect has one.
 *
 * @param labelId the filter's label id; a quick checkbox group takes its label as an id, not as text
 */
function quickFilterOf(filter: MainFilter, labelId: string): QuickFilter {
    if (filter.type === 'multiselect') {
        return { type: 'dropdown-multiselect', label: t(labelId), selector: filter.selector, options: filter.options };
    }
    if (filter.type === 'checkboxgroup') {
        return {
            selector: filter.selector,
            label: labelId,
            comperator: filter.comperator,
            multiple: filter.multiple,
            answerWithLabel: filter.answerWithLabel,
            filters: filter.filters,
        };
    }
    throw new Error(`A ${filter.type} filter has no quick filter: ${filter.selector}`);
}

/**
 * The quick filters of a report: shortcuts onto some of its own filters, picked by selector, in the order given.
 *
 * @param shortcuts each shortcut's selector, mapped to the filter's label id
 */
export function quickFiltersFrom(filters: MainFilter[], shortcuts: Record<string, string>): QuickFilter[] {
    return Object.entries(shortcuts).map(([selector, labelId]) => {
        const filter = filters.find((each) => each.selector === selector);
        if (!filter) {
            throw new Error(`The report has no filter to shortcut: ${selector}`);
        }
        return quickFilterOf(filter, labelId);
    });
}
